import { Money, type Currency, type JobCostResponsibility, type JobPriceSource, type RateBasis, type UpdateJobInput } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { useEffect } from 'react';
import { CommercialTermsFields } from './CommercialTermsFields';
import type { JobDetail } from '../../../jobs.api';

interface CommercialTermsFormValues {
  agreedPriceAmount?: number;
  agreedPriceCurrency?: Currency;
  rateBasis?: RateBasis;
  priceSource?: JobPriceSource;
  tollsPayer?: JobCostResponsibility;
  weighbridgePayer?: JobCostResponsibility;
  cancellationTerms?: string;
  paymentTermsDaysOverride?: number;
  poNumber?: string;
}

interface CommercialTermsFormProps {
  job: JobDetail;
  error: string | null;
  saving: boolean;
  canManage: boolean;
  onSave: (input: UpdateJobInput) => Promise<void>;
}

function toFormValues(job: JobDetail): CommercialTermsFormValues {
  const price = job.agreedPrice;
  return {
    agreedPriceAmount: price ? Number(Money.ofMinor(price.amountMinor, price.currency).toDecimalString()) : undefined,
    agreedPriceCurrency: price?.currency ?? 'KES',
    ...withoutNulls({
      rateBasis: job.rateBasis,
      priceSource: job.priceSource,
      tollsPayer: job.tollsPayer,
      weighbridgePayer: job.weighbridgePayer,
      cancellationTerms: job.cancellationTerms,
      paymentTermsDaysOverride: job.paymentTermsDaysOverride,
      poNumber: job.poNumber,
    }),
  };
}

function withoutNulls<T extends object>(fields: T): { [K in keyof T]?: NonNullable<T[K]> } {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== null && value !== undefined)) as { [K in keyof T]?: NonNullable<T[K]> };
}

function toAgreedPrice(values: CommercialTermsFormValues): UpdateJobInput['agreedPrice'] {
  const { agreedPriceAmount, agreedPriceCurrency } = values;
  if (agreedPriceAmount === undefined || agreedPriceAmount === null || !agreedPriceCurrency) return undefined;
  return Money.parse(String(agreedPriceAmount), agreedPriceCurrency).toJSON();
}

/** A blank field is left out, not sent as null: `PATCH /jobs/:id` leaves an absent field untouched, same as the Details tab. */
function toApiInput(values: CommercialTermsFormValues): UpdateJobInput {
  const { agreedPriceAmount: _amount, agreedPriceCurrency: _currency, cancellationTerms, poNumber, ...rest } = values;
  return {
    ...withoutNulls(rest),
    agreedPrice: toAgreedPrice(values),
    ...withoutNulls({ cancellationTerms: cancellationTerms?.trim() || null, poNumber: poNumber?.trim() || null }),
  };
}

/** What was agreed with the customer, recorded at intake so finance never has to invent it at invoicing (gitaumoses4/chukua#33). Read-only without `job.create`. */
export function CommercialTermsForm({ job, error, saving, canManage, onSave }: CommercialTermsFormProps) {
  const [form] = Form.useForm<CommercialTermsFormValues>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(job));
  }, [job, form]);

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Agreed terms</h2>
      <Form<CommercialTermsFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => onSave(toApiInput(values))}
        disabled={saving || !canManage}
        initialValues={toFormValues(job)}
      >
        {error && <Alert className="mb-5" type="error" title={error} showIcon />}
        <CommercialTermsFields customerPoNumberRequired={job.customerPoNumberRequired} customerPaymentTermsDays={job.customerPaymentTermsDays} />
        {canManage && (
          <Form.Item className="mb-0">
            <Button type="primary" htmlType="submit" loading={saving}>
              Save terms
            </Button>
          </Form.Item>
        )}
      </Form>
    </section>
  );
}
